import React, { useState } from 'react';
import { useListarEstabelecimentos, useCriarEstabelecimento, useAtualizarEstabelecimento } from '../../../hooks/useAdmin';
import { usePageTitle } from '../../../hooks/usePageTitle';
import { Estabelecimento } from '../../../types';
import Button from '../../../components/Common/Button';
import Input from '../../../components/Common/Input';
import { validateRequired, validatePhone, combineValidations } from '../../../utils/validations';

interface FormData {
  nome: string;
  endereco: string;
  telefone: string;
  quantidadeMesas: number;
}

const AdminEstabelecimentos: React.FC = () => {
  usePageTitle('Admin - Estabelecimentos');
  const { data: estabelecimentos, isLoading } = useListarEstabelecimentos();
  const { mutate: criar, isPending: criando } = useCriarEstabelecimento();
  const { mutate: atualizar, isPending: atualizando } = useAtualizarEstabelecimento();

  const [mostrarModal, setMostrarModal] = useState(false);
  const [editando, setEditando] = useState<Estabelecimento | null>(null);
  const [formData, setFormData] = useState<FormData>({ nome: '', endereco: '', telefone: '', quantidadeMesas: 0 });

  const abrirModalNovo = () => {
    setEditando(null);
    setFormData({ nome: '', endereco: '', telefone: '', quantidadeMesas: 0 });
    setMostrarModal(true);
  };

  const abrirModalEditar = (est: Estabelecimento) => {
    setEditando(est);
    setFormData({ nome: est.nome, endereco: est.endereco, telefone: est.telefone, quantidadeMesas: est.quantidadeMesas ?? 0 });
    setMostrarModal(true);
  };

  const fecharModal = () => {
    setMostrarModal(false);
    setEditando(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editando) {
      atualizar({ id: editando.id, ...formData }, { onSuccess: fecharModal });
    } else {
      criar(formData, { onSuccess: fecharModal });
    }
  };

  return (
    <div className="animate-fadeIn">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-4xl font-display font-bold text-neutral-900">Estabelecimentos</h1>
          <p className="text-neutral-500 text-sm mt-1">Gerencie os estabelecimentos do sistema</p>
        </div>
        <Button onClick={abrirModalNovo} variant="primary" size="md">
          Novo Estabelecimento
        </Button>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-xl border border-neutral-100 shadow-md p-6">
          <p className="text-neutral-500 text-center py-8">Carregando estabelecimentos...</p>
        </div>
      ) : estabelecimentos && estabelecimentos.length > 0 ? (
        <div className="grid gap-4">
          {estabelecimentos.map((est) => (
            <div key={est.id} className="bg-white rounded-xl border border-neutral-100 shadow-md p-6 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-neutral-900">{est.nome}</h2>
                <p className="text-sm text-neutral-500 mt-1">{est.endereco}</p>
                <p className="text-sm text-neutral-500">{est.telefone}</p>
                <p className="text-sm text-neutral-500">{est.quantidadeMesas} mesa{est.quantidadeMesas !== 1 ? 's' : ''}</p>
              </div>
              <Button onClick={() => abrirModalEditar(est)} variant="secondary" size="sm">
                Editar
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-neutral-100 shadow-md p-6">
          <p className="text-neutral-500 text-center py-8">Nenhum estabelecimento cadastrado</p>
        </div>
      )}

      {mostrarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl border border-neutral-100 shadow-md-xl p-6 w-full max-w-md">
            <h2 className="text-2xl font-display font-bold text-neutral-900 mb-6">
              {editando ? 'Editar Estabelecimento' : 'Novo Estabelecimento'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-5">
              <Input
                label="Nome *"
                type="text"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                placeholder="Nome do estabelecimento"
                onValidate={validateRequired('Nome')}
                validateOnBlur
              />

              <Input
                label="Endereço *"
                type="text"
                value={formData.endereco}
                onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                placeholder="Rua, número, bairro, cidade"
                onValidate={validateRequired('Endereço')}
                validateOnBlur
              />

              <Input
                label="Telefone *"
                type="text"
                value={formData.telefone}
                onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                placeholder="(11) 98765-4321"
                onValidate={combineValidations(validateRequired('Telefone'), validatePhone)}
                validateOnBlur
                helperText="Formato: (XX) XXXXX-XXXX"
              />

              <Input
                label="Quantidade de Mesas"
                type="number"
                min="0"
                value={formData.quantidadeMesas}
                onChange={(e) => setFormData({ ...formData, quantidadeMesas: parseInt(e.target.value) || 0 })}
                placeholder="0"
              />

              <div className="flex gap-3 pt-4 border-t border-neutral-100">
                <Button type="submit" variant="primary" size="md" fullWidth disabled={criando || atualizando}>
                  {criando || atualizando ? 'Salvando...' : editando ? 'Atualizar' : 'Criar'}
                </Button>
                <Button type="button" onClick={fecharModal} variant="secondary" size="md" fullWidth disabled={criando || atualizando}>
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEstabelecimentos;
